import { useState, useEffect } from 'react';
import { BarChart3, Zap } from 'lucide-react';
import { getSessionXp } from './XpFlashToast';

const PAGES_KEY = 'mw-session-pages';

export interface SessionPage {
  path: string;
  visitedAt: string;
}

function getSessionPages(): SessionPage[] {
  try {
    const raw = sessionStorage.getItem(PAGES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Record a page visit in the current session */
export function recordSessionPage(path: string): void {
  const pages = getSessionPages();
  if (pages.some((p) => p.path === path)) return;
  pages.push({ path, visitedAt: new Date().toISOString() });
  sessionStorage.setItem(PAGES_KEY, JSON.stringify(pages));
  window.dispatchEvent(new Event('session-progress-update'));
}

const SESSION_GOAL = 5; // articles per session milestone

export function SessionProgressBar() {
  const [pages, setPages] = useState(getSessionPages);
  const [xp, setXp] = useState(getSessionXp);

  useEffect(() => {
    const onProgress = () => setPages(getSessionPages());
    const onXp = () => setXp(getSessionXp());
    window.addEventListener('session-progress-update', onProgress);
    window.addEventListener('xp-earned', onXp);
    // Also listen for content visits (from ContinueLearningWidget tracking)
    window.addEventListener('content-visit-update', onProgress);
    return () => {
      window.removeEventListener('session-progress-update', onProgress);
      window.removeEventListener('xp-earned', onXp);
      window.removeEventListener('content-visit-update', onProgress);
    };
  }, []);

  const count = pages.length;
  if (count === 0) return null;

  const progress = Math.min(count, SESSION_GOAL);
  const pct = Math.round((progress / SESSION_GOAL) * 100);
  const done = progress >= SESSION_GOAL;

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-cyan-500/5 to-transparent">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-500/10">
          <BarChart3 className="h-4 w-4 text-cyan-500" />
        </div>
        <h3 className="font-semibold text-text-primary text-sm">Session Progress</h3>
        {xp > 0 && (
          <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-yellow-500">
            <Zap className="h-3 w-3" />
            {xp} XP
          </span>
        )}
      </div>

      <div className="px-4 py-3">
        <p className="text-sm text-text-secondary">
          {done
            ? 'Session milestone reached!'
            : `${count} of ${SESSION_GOAL} articles explored`}
        </p>

        <div className="mt-2 h-2 w-full rounded-full bg-bg-tertiary overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              done
                ? 'bg-gradient-to-r from-cyan-500 to-teal-400'
                : 'bg-gradient-to-r from-cyan-500 to-blue-400'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>

        <p className="mt-1.5 text-xs text-text-tertiary">
          {count} article{count !== 1 ? 's' : ''} this session
        </p>
      </div>
    </div>
  );
}
